import { WorkMassWorkStore } from './work-mass-work.store';
import { fb, goBack, translate } from '../../../../modules/shared/shared.global-service';
import { Component, computed, effect, EventEmitter, inject, input, Output, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    WorkDetailSelectServiceComponent,
} from '../../../medical-service/work/work-detail/work-detail-select-service/work-detail-select-service.component';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { DateInputComponent } from '../../../kit/input/date-input/date-input.component';
import { NumberInputComponent } from '../../../kit/input/text-input/number-input.component';
import {
    SelectPersonComponent,
} from '../../../../modules/content/components/common/select-person/select-person.component';
import { AppFormGroup, changeSignal, formAddText } from '../../../../utils/form.utils';
import { SelectInputComponent } from '../../../kit/input/select-input/select-input.component';
import {
    InputUserMultiselectComponent,
} from '../../../../modules/shared/components/inputs/input-user-multiselect/input-user-multiselect.component';
import { EmploymentParentGroupEnum } from '@libs/types/src/user/enum/employment-parent-group.enum';
import { isInvalid } from '../../../../modules/shared/helpers/validate';
import { dateifyOrNow } from '../../../../modules/shared/helpers/date.helper';
import { CreateWorkInput } from '@it2go/types';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import { checkOk } from '../../../../modules/content/content.error';
import { ActionButtonsComponent } from '../../../kit/action-buttons/action-buttons.component';
import { WorkOfferUpsertInput } from '@libs/types/src/services/input-type/work/work-offer.upsert-input';
import {
    SocialServiceSelectOfferComponent,
} from '../social-service-select-offer/social-service-select-offer.component';
import {
    SocialServiceSelectOfferTableComponent,
} from '../social-service-select-offer/social-service-select-offer-table/social-service-select-offer-table.component';
import { SocialServicePresenceTypeEnum } from '@libs/types/src/services/enum/social-service-presence-type.enum';
import {
    ServicePatternWysiwygComponent,
} from '../../../../modules/content/components/common/service-pattern-wysiwyg/service-pattern-wysiwyg.component';
import { ServicePatternStructureUsageEnum } from '@libs/types/src/services/enum/service-pattern-structure-usage.enum';
import { TagsInputComponent } from '../../../kit/input/tags-input/tags-input.component';
import { ButtonComponent } from '../../../kit/button/button.component';
import { MultiSelectInputComponent } from '../../../kit/input/multi-select-input/multi-select-input.component';
import { FileUploadBoxComponent } from '../../../kit/file-upload-box/file-upload-box.component';
import { DefaultCategoryTypeEnum } from '@libs/types/src/file-manager/types/default-category.type';
import { FileKindEnum } from '@libs/types/src/file-manager/enum/file-kind.enum';
import { ExternalType } from '@libs/types/src/file-manager/enum/external-type.enum';
import {
    WorkIndividualWorkOperationalReportWidgetComponent,
} from '../work-individual-work/work-individual-work-operational-report-widget/work-individual-work-operational-report-widget.component';
import { stripHtml } from '../../../../utils/string.utils';
import { workFolderUri } from '../../folder/works/folder-works-page.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CardComponent } from '../../../kit/card/card.component';
import { TranslatePipe } from '@ngx-translate/core';
import {
    WorkParticipantsFolder,
    WorkParticipantsSelectComponent,
} from '../work-participants-select/work-participants-select.component';
import { WorkCreditGroupComponent } from '../work-credit-group/work-credit-group.component';
import {
    WorkOfferTableItem,
} from '../social-service-select-offer/social-service-select-offer-table/social-service-select-offer-table.types';
import { SocialWorkPlanHistoryState } from '../social-works-plan/social-works-plan.types';

@Component({
    selector: 'app-work-mass-work',
    templateUrl: './work-mass-work.component.html',
    styleUrl: './work-mass-work.component.scss',
    standalone: true,
    providers: [WorkMassWorkStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        WorkDetailSelectServiceComponent,
        DateInputComponent,
        NumberInputComponent,
        SelectPersonComponent,
        SelectInputComponent,
        InputUserMultiselectComponent,
        ActionButtonsComponent,
        SocialServiceSelectOfferComponent,
        SocialServiceSelectOfferTableComponent,
        ServicePatternWysiwygComponent,
        TagsInputComponent,
        ButtonComponent,
        MultiSelectInputComponent,
        FileUploadBoxComponent,
        WorkIndividualWorkOperationalReportWidgetComponent,
        CardComponent,
        TranslatePipe,
        WorkParticipantsSelectComponent,
        WorkCreditGroupComponent,
    ],
})
export class WorkMassWorkComponent {

    public readonly store = inject(WorkMassWorkStore);
    protected readonly route = inject(ActivatedRoute);
    protected readonly router = inject(Router);

    protected readonly applicationsComponent = viewChild.required<NumberInputComponent>('applicationsComponent');
    protected readonly selectOfferComponent = viewChild(SocialServiceSelectOfferComponent);
    protected readonly offerTableComponent = viewChild<SocialServiceSelectOfferTableComponent>('offerTableComponent');
    protected readonly creditGroupComponent = viewChild(WorkCreditGroupComponent);
    protected readonly tagsInputComponent = viewChild<TagsInputComponent>('tagsInputComponent');
    protected readonly noteEditorComponent = viewChild(ServicePatternWysiwygComponent);
    protected readonly fileUploadComponent = viewChild(FileUploadBoxComponent);
    protected readonly operationalReportComponent = viewChild(WorkIndividualWorkOperationalReportWidgetComponent);

    public readonly personIdInput = input<number | null>(null);
    @Output() closeCallback = new EventEmitter<void>();

    protected prefix = 'workData.massWork.';
    protected iwPrefix = 'workData.individualWork.';

    protected form = new AppFormGroup({
        serviceId: fb.control<number | null>(null, Validators.required),
        personIds: fb.control<number[]>([], Validators.required),
        dateFrom: fb.control<Date | null>(new Date()),
        dateTo: fb.control<Date | null>(null),
        duration: fb.control<number>(0),
        users: fb.control<string[]>([]),
        form: fb.control<string | null>(null),
        presence: fb.control<string | null>(SocialServicePresenceTypeEnum.PRESENT),
        note: fb.control<string>(''),
        tags: fb.control<string[]>([]),
        offers: fb.control<WorkOfferUpsertInput[]>([], Validators.required),
        subOffers: fb.control<WorkOfferTableItem[]>([], { nonNullable: true }),
        copyToOfferIds: fb.control<number[]>([]),
        participantIds: fb.control<number[]>([]),
    });

    protected serviceIdChange = changeSignal(this.form.controls.serviceId);
    protected dateFromChange = changeSignal(this.form.controls.dateFrom);
    protected dateToChange = changeSignal(this.form.controls.dateTo);
    protected personIdsChange = changeSignal(this.form.controls.personIds);
    protected offersCodeChange = this.form.signalCodeChange<WorkOfferUpsertInput[]>('offers');
    protected personsCodeChange = this.form.signalCodeChange<number[]>('personIds');

    protected hasMultipleOffers = computed(() => (this.offersCodeChange().value?.length || 0) > 1);
    protected readonly canSave = this.store.isAllowed('workMassWork.create');
    private readonly planState = history.state?.calendarCorrelationId
        ? history.state as SocialWorkPlanHistoryState
        : null;

    protected copyOfferOptions = computed(() => {
        const offers = this.offersCodeChange().value || [];
        const allOptions = this.selectOfferComponent()?.offerOptions() || [];
        const selectedIds = new Set(offers.map(it => it.offerId));
        return allOptions
            .filter(it => selectedIds.has(it.offerId))
            .map(it => ({ name: `${it.name} (${it.code})`, value: it.offerId }));
    });


    public allowedForms = computed(() => {
        const forms = this.store.globalGlobalStore.organizationStore
            .getServiceById(this.serviceIdChange() || 0)?.()?.form || [];
        return forms.map((it) => ({
            name: translate.instant(`service.serviceFormEnum.${it}`),
            value: it,
        }));
    });

    protected readonly participantsComponent = viewChild(WorkParticipantsSelectComponent);

    protected readonly folderIds = computed(() => this.selectedFolders().value.map(folder => folder.id));

    protected participantFolders = computed<WorkParticipantsFolder[]>(() =>
        this.selectedFolders().value.map((it) => ({ id: it.id, person: it.person })),
    );

    protected selectedFolders = computed(() => {
        const personIds = this.personsCodeChange();
        const serviceId = this.serviceIdChange();
        return {
            isCodeUpdate: personIds.isCodeUpdate,
            value: this.store.folders().filter((it) =>
                it.serviceId == serviceId && (personIds.value || []).includes(it.personId),
            ),
        };
    });

    constructor() {

        this.store.personIdFromQuery = this.route.snapshot.queryParams['personId'];
        if (this.store.personIdFromQuery) {
            this.form.patchValue({ personIds: [Number(this.store.personIdFromQuery)] }, { isCodeUpdate: true });
        }

        if (this.planState) {
            this.form.patchValueCode({
                serviceId: this.planState.serviceId,
                personIds: this.planState.personIds,
                dateFrom: this.planState.dateFrom,
                dateTo: this.planState.dateTo,
                users: this.planState.userIds,
                note: this.planState.note,
            });
        }

        effect(() => {
            const selectOffer = this.selectOfferComponent();
            if (selectOffer && this.planState) {
                selectOffer.preselectedOffers.set(this.planState.offers);
            }
        });

        effect(() => {
            const personId = this.personIdInput();
            if (personId && this.personIdsChange()?.length === 0) {
                this.form.patchValue({ personIds: [personId] }, { isCodeUpdate: true });
            }
        });

        effect(() => {
            const from = this.dateFromChange();
            const to = this.dateToChange();
            if (!from || !to) return;

            const duration = Math.round((to.getTime() - from.getTime()) / 60_000);
            this.form.patchValue({ duration });
            this.applicationsComponent().setValue(duration);
            this.selectOfferComponent()?.setApplicationsByDesiredDuration(duration);
        });

        effect(async () => {
            const userId = this.store.globalGlobalStore.loggedUserStore.currentUserId();
            if (!userId || this.form.value.users?.length) return;
            this.form.patchValue({ users: [userId] });
        });

        effect(() => {
            this.store.serviceId.set(this.serviceIdChange() || 0);
        });

        effect(() => {
            const personIds = this.personIdsChange() || [];
            const serviceId = this.serviceIdChange() || 0;
            personIds.forEach((personId) => this.store.fetchFolder(personId, serviceId));
        });

        // Build subOffers as cross-product of (offers × folders), preserving existing rows.
        effect(() => {
            const currentSubOffers = [...(this.form.value.subOffers || [])];
            const offersChange = this.offersCodeChange();
            const selectedFoldersChange = this.selectedFolders();
            const offers = offersChange?.value || [];
            const folders = selectedFoldersChange?.value || [];

            if (offersChange.isCodeUpdate && selectedFoldersChange.isCodeUpdate) return;
            if (!offersChange.value || !selectedFoldersChange.value) return;

            const folderCount = folders.length || 1;
            const newSubOffers: any[] = [];
            folders.forEach((folder, folderIndex) => {
                offers.forEach((offer) => {
                    const existing = currentSubOffers.find((it: any) =>
                        it.offerId == offer.offerId && it.folder?.id == folder.id,
                    );
                    const base = Math.floor(offer.applications / folderCount);
                    const modulo = offer.applications % folderCount;
                    const applications = base + (folderIndex < modulo ? 1 : 0);
                    newSubOffers.push({
                        ...(existing ?? offer),
                        applications,
                        folder,
                    });
                });
            });

            this.form.patchValueCode({ subOffers: newSubOffers });
            this.creditGroupComponent()?.refreshCreditOffers(newSubOffers);
        });
    }

    protected queryTags(search: string): void {
        this.store.api.searchWorkOfferTags({
            search,
            limit: 100,
            serviceId: this.form.value.serviceId,
        });
    }

    protected setDuration(duration?: number): void {
        const from = this.dateFromChange();
        if (!from) return;

        const dateTo = new Date(from);
        dateTo.setMinutes(dateTo.getMinutes() + (duration || 0));
        this.form.patchValue({ dateTo });
    }

    protected setOffers(offers: WorkOfferUpsertInput[]): void {
        const from = this.dateFromChange();
        if (!from) return;

        let duration = this.selectOfferComponent()!.isDurationAltered
            ? offers.reduce((acc, it) => acc + it.applications, 0)
            : this.form.value.duration;
        if (duration == 0) {
            const options = this.selectOfferComponent()!.selectedOfferOptions();
            duration = options.reduce((acc, it) => acc + (it.usualApplications || 1), 0);
        }

        const dateTo = new Date(from);
        dateTo.setMinutes(dateTo.getMinutes() + (duration || 0));
        this.form.patchValueCode({ dateTo });
    }

    protected copyNoteToSubOffers(): void {
        const note = this.form.value.note || '';
        const tags = [...(this.form.value.tags || [])];
        const toIds = this.form.value.copyToOfferIds || [];
        const updatedSubOffers = (this.form.value.subOffers || []).map((it: any) => {
            if (toIds.length > 0 && !toIds.includes(it.offerId)) return it;
            return { ...it, note, tags };
        });
        this.form.patchValueCode({
            subOffers: updatedSubOffers,
            note: '',
            tags: [],
            copyToOfferIds: [],
        });
        this.noteEditorComponent()?.wysiwygEditor()?.setContent('');
        this.tagsInputComponent()?.setValue([]);
        this.offerTableComponent()?.refreshTable(true);
    }

    protected async save(): Promise<void> {
        if (!this.canSave() || this.creditGroupComponent()?.insufficientBalance()) return;
        const opForm = this.operationalReportComponent()!.form;
        if (isInvalid(this.form, opForm)) return;

        const folderItems = this.offerTableComponent()?.items() || [];
        const byFolder = new Map<number, any[]>();
        folderItems.forEach((item: any) => {
            const folderId = item.folder?.id;
            if (folderId == null) return;
            if (!byFolder.has(folderId)) byFolder.set(folderId, []);
            byFolder.get(folderId)!.push(item);
        });

        const folders = this.store.folders().filter((f) =>
            [...byFolder.keys()].includes(f.id),
        );

        const metadata = {
            categoryCode: DefaultCategoryTypeEnum.INDIVIDUAL_WORK,
            note: null,
            date: new Date(),
            kind: FileKindEnum.received,
            accesses: folders.length
                ? folders.map((f) => ({
                    externalType: ExternalType.folder,
                    externalId: String(f.id),
                }))
                : null,
        };

        const fileIds = (await this.fileUploadComponent()?.uploadFiles(metadata)) || [];

        const hasOpReport = stripHtml(opForm.value.description || '').trim().length > 0;

        let currentFrom = dateifyOrNow(this.form.value.dateFrom);

        // Účastníci se vybírají napříč všemi složkami, ale každá práce dostane jen ty své.
        const folderIdByPersonId = this.participantsComponent()?.folderIdByPersonId() ?? new Map();
        const selectedParticipantIds = this.form.value.participantIds || [];

        for (const [folderId, offerItems] of byFolder.entries()) {
            const workDuration = offerItems.reduce((sum: number, it: any) => sum + (it.applications || 0), 0);
            const workDateFrom = new Date(currentFrom);
            const workDateTo = new Date(currentFrom);
            workDateTo.setMinutes(workDateTo.getMinutes() + workDuration);
            currentFrom = workDateTo;

            const data: CreateWorkInput = {
                workType: WorkTypeEnum.INDIVIDUAL_WORK,
                calendarCorrelationId: this.planState?.calendarCorrelationId,
                serviceId: this.form.value.serviceId,
                folderId,
                dateFrom: workDateFrom.toISOString(),
                dateTo: workDateTo.toISOString(),
                userIds: this.form.value.users || [],
                fileIds,
                workData: JSON.stringify({
                    note: this.form.value.note,
                    form: this.form.value.form,
                    presence: this.form.value.presence,
                    type: WorkTypeEnum.MASS_WORK,
                }),
                participantIds: selectedParticipantIds
                    .filter((it) => folderIdByPersonId.get(Number(it)) === folderId),
                offers: offerItems.map((it: any) => ({
                    offerId: it.offerId,
                    payByCredits: it.payByCredits,
                    applications: it.applications,
                    dateFrom: it.dateFrom,
                    dateTo: it.dateTo,
                    executions: it.executions,
                    procedures: it.procedures,
                    operations: it.operations,
                    durationNote: it.durationNote,
                    durationPreparation: it.durationPreparation,
                    durationSelfReflection: it.durationSelfReflection,
                    presence: it.presence,
                    type: it.type,
                    note: it.note || '',
                    users: it.users || [],
                    tags: it.tags || [],
                    areas: it.areas || [],
                    targets: it.targets || [],
                    projects: it.projects || [],
                })),
            };
            const res = await this.store.api.createWork(data, 'id');
            if (!checkOk(res, 'create')) {
                return;
            }

            if (hasOpReport) {
                const opData: CreateWorkInput = {
                    workType: WorkTypeEnum.OPERATIONAL_REPORT,
                    dateFrom: workDateFrom.toISOString(),
                    dateTo: workDateFrom.toISOString(),
                    userIds: [this.store.globalGlobalStore.loggedUserStore.currentUserId() as string],
                    serviceId: this.form.value.serviceId,
                    folderId,
                    parentWorkId: (res as any).id,
                    workData: JSON.stringify({
                        sendEmail: opForm.value.sendEmail,
                        type: opForm.value.type,
                        form: this.form.value.form,
                        priority: opForm.value.priority,
                        description: opForm.value.description || '',
                        users: opForm.value.userIds || [],
                    }),
                };
                await this.store.api.createWork(opData, 'id');
            }
        }

        await this.fileUploadComponent()?.confirmFiles(fileIds);
        this.close();
    }

    public close() {
        // opened as modal
        if (this.personIdInput()) {
            this.closeCallback.emit();
            // opened from folder
        } else if (this.store.personIdFromQuery) {
            this.router.navigateByUrl(workFolderUri(WorkTypeEnum.INDIVIDUAL_WORK, this.store.currentFolderId));
        } else {
            if (this.closeCallback.observed) {
                this.closeCallback.emit();
            } else if (this.planState?.returnUrl) {
                void this.router.navigateByUrl(this.planState.returnUrl);
            } else {
                goBack();
            }
        }
    }

    protected readonly WorkTypeEnum = WorkTypeEnum;
    protected readonly ServiceTypeEnum = ServiceTypeEnum;
    protected readonly EmploymentParentGroupEnum = EmploymentParentGroupEnum;
    protected readonly ServicePatternStructureUsageEnum = ServicePatternStructureUsageEnum;
    protected readonly formAddText = formAddText;
    protected readonly goBack = goBack;
}
